import React from "react";
import {
  components,
  MultiValueRemoveProps,
  OptionProps,
  DropdownIndicatorProps,
  ClassNamesConfig,
  GroupBase,
  StylesConfig,
} from "react-select";
import tokens from "common/theme/tokens.json";

export const labelClasses = "text-secondary-700";

/**
 * Ceiling on how wide an open menu may grow when its options are sized to their content.
 *
 * Deviation from `architecture.md` ("spacing, colors, and breakpoints come from tokens"): `tokens.json`
 * carries no width or max-width family at all — only colours, type, breakpoints, a spacing scale, shadow,
 * and cards — so there is no token to draw on. A named constant is the documented substitute, as with
 * `GLOBAL_CALENDAR_USER_FILTER_MENU_Z`.
 */
export const MENU_MAX_WIDTH = "28rem";

/** Space left between an open menu and the viewport edges. */
export const MENU_VIEWPORT_GUTTER_PX = parseInt(tokens.Spacing.lg.value, 10);

/**
 * Room remaining to the right of the control, published by `DropdownMenuPortal`'s Floating UI `size`
 * middleware onto the portal wrapper and read by the menu to cap its own max-width.
 */
export const MENU_AVAILABLE_WIDTH_VAR = "--dropdown-menu-available-width";

/**
 * Room remaining below the control (or above, when flipped), published alongside
 * `MENU_AVAILABLE_WIDTH_VAR` and read by the menu list to cap its scroll height.
 */
export const MENU_AVAILABLE_HEIGHT_VAR = "--dropdown-menu-available-height";

export const MultiValueRemove = (props: MultiValueRemoveProps<any>) => {
  return (
    <components.MultiValueRemove {...props}>
      <span className={"material-symbols-outlined text-sm"}>close</span>
    </components.MultiValueRemove>
  );
};

export const CustomOption = (props: OptionProps): React.JSX.Element => {
  const typedProps = props as OptionProps<{ name: string }>;
  return (
    <components.Option {...props}>
      <div className="flex">
        <div className="mr-auto">{typedProps.data.name}</div>
        {/* The icon font sets `line-height: 1` and arbitrary `text-[18px]` sets only font-size, so
            without an explicit leading the selected row's line box grows to 18px and the row lands at
            30px against its neighbours' 28px — `min-h-[28px]` is a floor, not a clamp. */}
        {props.isSelected && (
          <span className="material-symbols-outlined text-secondary-900 text-[18px] leading-[16px]">check</span>
        )}
      </div>
    </components.Option>
  );
};

export const DropdownIndicator = (props: DropdownIndicatorProps) => {
  let classes = "material-symbols-outlined";
  if (props.isDisabled) {
    classes = `${classes} text-secondary-200`;
  } else {
    classes = `${classes} text-secondary-300`;
  }
  let icon = "expand_more";
  if (props.selectProps.menuIsOpen) {
    icon = "expand_less";
  }
  return <span className={classes}>{icon}</span>;
};

export const dropdownCustomComponents = {
  IndicatorSeparator: () => null,
  DropdownIndicator,
  MultiValueRemove,
  Option: CustomOption,
};

export const classNames: ClassNamesConfig<unknown, boolean, GroupBase<unknown>> = {
  valueContainer: () => {
    return "!p-null gap-sm";
  },
  placeholder: (state) => {
    let classes = "text-sm leading-[16px] !p-0";
    if (state.isDisabled) {
      classes = `${classes} !text-secondary-200`;
    } else {
      classes = `${classes} !text-secondary-500`;
    }
    return classes;
  },
  input: (state) => {
    let classes = "!text-sm !border-null leading-[16px] !p-0 !ring-0 focus:!border-null focus:!ring-null";
    if (state.isDisabled) {
      classes = `${classes} !text-secondary-200`;
    } else {
      classes = `${classes} !text-secondary-900`;
    }
    return classes;
  },
  singleValue: (state) => {
    let classes = "text-sm leading-[16px] !p-0";
    if (state.isDisabled) {
      classes = `${classes} !text-secondary-200`;
    } else {
      classes = `${classes} !text-secondary-900`;
    }
    return classes;
  },
  container: () => {
    return "!ring-0";
  },
  control: (state) => {
    const hasError = (state.selectProps as any)?.hasError;

    let style = "!rounded-3xl !px-sm !py-[7px] !min-h-[36px] hover:!border-secondary-300 !ring-0";

    if (state.isDisabled) {
      style = `${style} !bg-secondary-50`;
    } else if (hasError) {
      style = `${style} !border-error-400`;
    } else {
      style = `${style} !border-secondary-100`;
    }

    if (state.menuIsOpen) {
      if (hasError) {
        return `!border-error-400 focus-within:!ring-error-50 ${style}`;
      }
      return `!border-secondary-300 ${style}`;
    }

    if (hasError) {
      return `focus-within:!border-error-400 focus-within:!ring-error-50 ${style}`;
    }

    return `focus-within:!border-secondary-300 focus-within:!ring-secondary-50 ${style}`;
  },
  multiValue: (state) => {
    let classes = "!text-sm !rounded-full !pl-sm !pr-xs !py-0 h-[20px] !m-null";
    if (state.isDisabled) {
      classes = `${classes} !bg-secondary-50`;
    } else {
      classes = `${classes} !bg-secondary-100`;
    }
    return classes;
  },
  multiValueRemove: (state) => {
    let classes = "!rounded-full hover:!bg-secondary-200 m-[2px] !p-[2px] mr-[0px]";
    if (state.isDisabled) {
      classes = `${classes} !text-secondary-200`;
    } else {
      classes = `${classes} !text-secondary-900`;
    }
    return classes;
  },
  multiValueLabel: (state) => {
    let classes = "!text-sm !p-0 flex items-center";
    if (state.isDisabled) {
      classes = `${classes} !text-secondary-200`;
    } else {
      classes = `${classes} `;
    }
    return classes;
  },
  menuList: () => {
    return "!p-sm gap-xs flex flex-col !text-sm";
  },
  option: (state) => {
    // min-h, not h: an option whose label wraps must grow rather than overflow onto its neighbour.
    // 6 + 16 (leading) + 6 = 28px, so a single-line option keeps its height.
    const style = "!text-secondary-900 hover:!bg-secondary-50 leading-[16px] min-h-[28px] !px-xs !py-[6px]";
    if (state.isFocused) {
      return `!bg-secondary-50 ${style}`;
    }
    if (state.isSelected) {
      return `!bg-secondary-50 ${style}`;
    }
    return style;
  },
  menu: () => {
    return "!shadow-cards/shadow !rounded-3xl";
  },
  indicatorsContainer: () => {
    return "!h-[16px]";
  },
  menuPortal: () => {
    return "!z-[1050]";
  },
};

/**
 * Emotion overrides that pull the menu out of react-select's `absolute` flow so it flows inside
 * `DropdownMenuPortal`'s Floating UI wrapper, which can only flip/size a wrapper that has the menu's
 * real dimensions. Sized to content, floored at the control width, capped by the room vars.
 */
export const dropdownStyles: StylesConfig<unknown, boolean, GroupBase<unknown>> = {
  menu: (base) => ({
    ...base,
    position: "relative",
    top: "auto",
    bottom: "auto",
    marginTop: 0,
    marginBottom: 0,
    width: "max-content",
    minWidth: "100%",
    maxWidth: `min(${MENU_MAX_WIDTH}, var(${MENU_AVAILABLE_WIDTH_VAR}, 100vw))`,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: `var(${MENU_AVAILABLE_HEIGHT_VAR}, ${base.maxHeight}px)`,
  }),
};

/**
 * Layers a caller's `styles` on top of the shared ones per key, so passing `selectProps.styles`
 * overrides only the keys it names instead of dropping the shared width rules entirely.
 *
 * The merge is per key, not composing: a caller that names `menu` or `menuList` still silently
 * drops the shared width/height clamps for that key.
 *
 * @param callerStyles - `styles` supplied through a call site's `selectProps`
 * @returns the merged config to hand to react-select
 */
export const mergeStyles = (
  callerStyles?: StylesConfig<unknown, boolean, GroupBase<unknown>>,
): StylesConfig<unknown, boolean, GroupBase<unknown>> => ({ ...dropdownStyles, ...callerStyles });
